import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
  additionalSection3?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'The Path to Inner Stillness',
    content: [
      'This module focuses on three key areas that shift your life from being frantic to being centered.',
    ],
    additionalSection: {
      title: '1. Acceptance and Surrender',
      content: [
        'In our busy world, we\'re taught to constantly fight, control, and fix things. Spiritually, acceptance and surrender are your greatest strengths because they stop you from wasting energy on battles you can\'t win.',
        '• <strong>Acceptance</strong> is simply acknowledging reality: "This is what is happening right now." It\'s not giving up, but wisely choosing where to spend your energy.',
        '• <strong>Surrender</strong> means letting go of your need for things to be different than they are. When you stop resisting the present moment, the conflict drops, and peace naturally takes its place.',
      ]
    },
    additionalSection2: {
      title: '2. Aligning with Your Inner Truth',
      content: [
        'True peace is impossible if you\'re living in conflict with yourself. The core work here is making sure your daily choices match your deepest values. If you value kindness, but spend your day gossiping, you will feel restless. When you make the effort to live with integrity–when your actions align with your truth–your mind settles because it\'s no longer fighting itself.',
      ]
    },
    additionalSection3: {
      title: '3. Building Habits of Rest and Reflection',
      content: [
        'Chaos thrives when you\'re exhausted and mindlessly rushing. To counteract this, you must deliberately incorporate stillness:',
        '• <strong>Reflection</strong> gives you perspective. It\'s time to process life\'s events and integrate lessons, preventing the buildup of stress and emotional "clutter."',
        '• <strong>Rest</strong> goes beyond sleep. It means consciously stepping away from the demands of doing and achieving. This downtime restores your inner resilience and makes you strong enough to face the storms when they come.',
      ]
    },
  },
  {
    title: 'Simple Tools for Sustainable Calm',
    content: [
      'These practices are your go-to tools for returning to your anchor anytime, anywhere.',
      '• <strong>Centering Breath:</strong> This is your fastest emergency brake. When you feel tension rising, consciously use your breath to reset. Inhale peace, exhale tension. This simple, physical act immediately connects your mind and body to the present moment, interrupting the stress cycle.',
      '• <strong>Values Alignment:</strong> Write down the 3 things that matter most to you (e.g., connection, health, creativity). Every week, look at your actions and ask: Did I spend time and energy supporting these? If not, you know where to make changes to restore harmony.',
      '• <strong>Stillness Ritual:</strong> Dedicate just 10 minutes in silence daily. Don\'t try to think or solve problems. Just sit. Over time, you\'ll start to notice a quiet, steady calm that exists beneath the surface of your thoughts. This stillness is the core of your inner peace.',
      '<br/>',
      '<strong>Key Message:</strong> Harmony, ultimately, is defined as the internal state where your soul and your life are working together, accepting what you can\'t change and wisely acting on what you can.'
    ],
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about inner peace and finding harmony.',
        'The quiz will help you review key concepts about acceptance, trust, alignment with values, and cultivating sustainable calm.'
      ]
    }
  },
];

// Main component, matching the design of the standard assessment form
const SpiritualModule6Page = () => {
  const navigate = useNavigate();
  const { currentStep: rawCurrentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM6');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within valid bounds
  const currentStep = Math.min(rawCurrentStep, totalSteps - 1);

  const currentModule = moduleContent[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep);
    } else {
      // Mark the module as completed and navigate to quiz
      await saveProgress(currentStep, true);
      navigate('/panel/learn/spiritual-module-6/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/spiritual-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep);
  };

  const isCompleted = currentStep === totalSteps - 1;

  return (
    // 1. Updated wrapper class: module-form-page -> module-form
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        {/* 2. Updated left panel class: module-left-panel -> module-left */}
        <div 
          className="module-left spiritual-panel"
        >
          {/* 3. Retaining module-header for left panel content */}
          <div className="module-header">
            <h1>Module 6</h1>
            <p className="module-subtitle">Inner Peace – Finding Harmony Amid Chaos</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
            Inner Peace isn't a goal you reach when all your problems disappear; it's the presence of calm that you build inside, so you can handle chaos without being swept away. 
            Think of it as developing an unshakeable anchor deep within yourself. 
            This peace grows from three powerful sources: acceptance, trust, and alignment with your deepest values.
            </p>
            <p className="module-tool-info">
            The key message here is vital: Peace is cultivated, not found. 
            You don't wait for it to arrive; you practice bringing it forth, learning to stop fighting what is and start trusting the flow of your life.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        {/* 4. Updated right panel class: module-right-panel -> module-right */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {` ${currentModule.title}`}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection3 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection3.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection3.content.map((paragraph: string, index: number) => (
                    <p key={`additional3-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              // Logic moved to handleBack, so button is never disabled until the end state is reached.
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Back'}
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
            >
              {isCompleted ? 'Take the Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule6Page;

